/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useRef } from 'react';
import _ from 'lodash';
import { getFullScore, getProjectScore, notApplicable, userErrorMessage, showErrorToast } from 'helpers';
import { PREV_TCAS_LABEL } from 'constants/school-year';
import cn from 'classnames';

// Components
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Hooks
import { useSelector } from 'react-redux';
import useAdmission from 'hooks/round3/useAdmission';
import { hasValidPriorities, PRIORITY_ERROR, MISSING_COURSE_ERROR, MISSING_COURSE_LABEL } from 'hooks/round3/priorities';
import useIsMobile from 'hooks/useIsMobile';
import PassChance from './PassChance';
import { LATE_FEE } from 'late-fee';

const AdmissionEdit = ({ favorites, onSavePriorities, mutationBusy = false, studentStatus, onModalOpen, onSaveSuccess, onDragEnd, onRemovePriority, onBack, quotaUsed, redeemableAmount, phase }) => {
  const dragDropRef = useRef();

  const profile = useSelector((state) => _.get(state, 'app.user') || {});
  const courseMap = useSelector((state) => _.get(state, 'app.courseMap')) || {};
  const admissionCountMap = useSelector((state) => _.get(state, 'app.admissionCountMap')) || {};
  const { projects, availables, nextPriority } = useAdmission(favorites, studentStatus, { draft: true });

  const [errorActive, setErrorActive] = useState(false);
  const [height, setHeight] = useState(0);
  const isMobile = useIsMobile();
  const _onSave = async () => {
    if (mutationBusy) return;
    if (disabled) {
      showErrorToast(PRIORITY_ERROR);
      return;
    }
    const priorities = projects.reduce((map, { _id, priority }) => ({ ...map, [_id]: priority }), {});
    try {
      const saved = await onSavePriorities(priorities);
      if (saved) onSaveSuccess(saved);
    } catch (error) {
      console.log({ error });
      showErrorToast(userErrorMessage(error));
    }
  }

  const validPriorities = hasValidPriorities(projects, { allowEmpty: true }) && hasValidPriorities(projects, { allowEmpty: true, idField: '_id' });
  const missingCourse = projects.some(({ program_project }) => !courseMap[program_project]);
  const disabled = mutationBusy || !validPriorities || projects.some(({ program_project, priority }) => {
    const project = courseMap[program_project];
    if (!project) return true;
    const errors = notApplicable(profile, project, { studentStatus, favorites: projects, nextPriority: priority });
    return errors.length;
  });
  return (
    <>
      <p>{'กรุณาเลือกสาขาวิชาที่จะสมัคร เรียงตามความสนใจ สามารถเลือกได้สูงสุด 10 อันดับ'}</p>
      {!validPriorities && <p role="alert" className="color-red">{PRIORITY_ERROR}</p>}
      {missingCourse && <p role="alert" className="color-red">{MISSING_COURSE_ERROR}</p>}
      <ul ref={dragDropRef} className="r-choose -drag" style={height ? { height } : null}>
        <DragDropContext
          onBeforeDragStart={() => setHeight((dragDropRef.current || {}).offsetHeight || 0)}
          onDragEnd={(result) => { onDragEnd(projects, result, courseMap); setHeight(0); }}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}>
                {(projects || []).map((row, index) => {
                  const lang = 'th';
                  const project = courseMap[row.program_project];
                  const majorName = _.get(project, `major_name_${lang}`);
                  const projectName = _.get(project, `project_name_${lang}`);
                  // หลักสูตรหาย — ต้องยังเห็นแถว + ปุ่มถอด ไม่งั้นบันทึกถูกบล็อกแต่ถอดไม่ได้
                  if (!project) {
                    return (
                      <Draggable key={row._id} draggableId={row._id} index={index} isDragDisabled={mutationBusy}>
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} style={{ useSelect: 'none', ...provided.draggableProps.style }}>
                            <li className="error">
                              <label style={{ pointerEvents: 'initial', ...(isMobile ? { paddingBottom: 60 } : {}) }}>
                                <div className="order">{'อันดับที่'} {row.priority}</div>
                                <i className="i-drag" />
                                <strong className="color-red">{MISSING_COURSE_LABEL}</strong>
                              </label>
                              <i className="i-del cursor-pointer" role="button" aria-label="ถอดออกจากอันดับ" onClick={() => onRemovePriority(row)} />
                            </li>
                          </div>
                        )}
                      </Draggable>
                    );
                  }

                  let errors = notApplicable(profile, project, { studentStatus, favorites: projects, nextPriority: row.priority });
                  const admissionCountKey = `${project.program_id || 0}_${project.major_id || 0}_${project.project_id || 0}`;
                  return (
                    <Draggable key={project._id} draggableId={project._id} index={index} isDragDisabled={mutationBusy}>
                      {(provided) => {
                        return (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{ useSelect: 'none', ...provided.draggableProps.style }}>
                            <li className={cn({ error: Boolean(errors.length) })}>
                              <label style={{ pointerEvents: 'initial', ...(isMobile ? { paddingBottom: 60 } : {}) }}>
                                <div className="order">{'อันดับที่'} {row.priority}</div>
                                <h4 className="score" style={isMobile ? { bottom: 38 } : {}}>
                                  {'คะแนน'} {getProjectScore(profile, project, row.priority)}/{getFullScore(project)}
                                  {!errors.length && (
                                    <PassChance score={getProjectScore(profile, project, row.priority)} project={project} />
                                  )}
                                </h4>
                                <h4
                                  className="score"
                                  style={isMobile
                                    ? { right: 'auto', left: 'auto', bottom: 16, textAlign: 'left', pointerEvents: 'none' }
                                    : { right: 16, left: 'auto', pointerEvents: 'none' }}
                                >
                                  {`จำนวนผู้สมัคร ${(admissionCountMap[admissionCountKey] || 0).toLocaleString()} คน`}
                                  {Number(_.get(project, 'last_year_register')) ? (
                                    <span style={{ color: '#999' }}>{` (${PREV_TCAS_LABEL} : ${Number(_.get(project, 'last_year_register')).toLocaleString()} คน)`}</span>
                                  ) : null}
                                </h4>
                                <i className="i-drag" />
                                <span
                                  className="u-logo"
                                  style={{ backgroundImage: `url('/assets/img/logo/${_.get(project, 'university_id')}.png')` }}
                                />
                                <strong>{_.get(project, `university_name_${lang}`)}</strong>
                                {_.get(project, `campus_name_${lang}`)}
                                <br />
                                {_.get(project, `faculty_name_${lang}`)}
                                <br />
                                {_.get(project, `field_name_${lang}`)}
                                <br />
                                {majorName && majorName !== '0' ? (
                                  <>
                                    {'วิชาเอก'}
                                    {majorName}
                                    <br />
                                  </>
                                ) : null}
                                {_.get(project, `program_name_${lang}`)}
                                <br />
                                {lang === 'th' ? `(${_.get(project, `program_type_name_th`)})` : null}
                                {projectName && projectName !== '0' ? (
                                  <>
                                    <br />
                                    {'โครงการ '}
                                    {projectName}
                                  </>
                                ) : null}
                              </label>
                              {Boolean(errors.length) && (
                                <div className={cn('t-collapse', { active: errorActive })}>
                                  <div className="t-toggle" onClick={() => setErrorActive(_active => !_active)}>
                                    {'ไม่ผ่านเงื่อนไขการรับ ไม่สามารถสมัครได้'}<i className="i-toggle i-down-red"></i>
                                  </div>
                                  <div className="t-content">
                                    <ul>
                                      {errors.map(({ messages = [], key, label, expected, value }) => {
                                        if (messages.length > 1) {
                                          return (
                                            <li key={key} style={{ padding: 0 }}>
                                              <i className="i-no"></i>
                                              <span>{messages[0].label}</span>
                                              {messages.map(({ expected, value }, index) => (
                                                <div key={index}>
                                                  <b>{expected}</b>
                                                  <span>({'ผู้สมัคร'}: <b className="color-red">{value}</b>)</span>
                                                </div>
                                              ))}
                                            </li>
                                          )
                                        }
                                        if (!value && value !== 0) {
                                          return (
                                            <li key={key} style={{ padding: 0 }}>
                                              <i className="i-no"></i>
                                              <span>{label}</span>
                                            </li>
                                          )
                                        }
                                        return (
                                          <li key={key} style={{ padding: 0 }}>
                                            <i className="i-no"></i>
                                            <span>{label}</span>
                                            <b>{expected}</b>
                                            <span>({'ผู้สมัคร'}: <b className="color-red">{value}</b>)</span>
                                          </li>
                                        )
                                      })}
                                    </ul>
                                  </div >
                                </div >
                              )}
                              <i
                                className="i-del cursor-pointer"
                                role="button"
                                aria-label="ถอดออกจากอันดับ"
                                onClick={() => onRemovePriority(row)} />
                            </li>
                          </div>
                        );
                      }}
                    </Draggable>
                  )
                })}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </ul>
      {Boolean(redeemableAmount) && (
        <div className="r-choose" style={{ margin: '0 -10px' }}>
          <li className="noti _heading">
            <strong className="color-orange" style={{ fontSize: '1.2em' }}>
              <span>สมัครครั้งแรก</span>
              <span style={{ textDecoration: 'underline' }}>{phase === 'late' ? 'ไม่มีค่าสมัคร' : 'ไม่มีค่าใช้จ่าย'}</span>
            </strong>
            {/* สมัครล่าช้า (ทปอ. 17 ต.ค. 69) — 200 ต่อการยืนยัน แม้ อว. จ่ายค่าสมัครครบ */}
            {phase === 'late' && <div className="color-red">{`ช่วงสมัครล่าช้า ชำระเฉพาะค่าธรรมเนียมสมัครล่าช้า ${LATE_FEE} บาท`}</div>}
            <div>ผู้สมัครขอรับเงินสนับสนุนค่าสมัครคัดเลือก รอบที่ 3 Admissions ได้ตามจำนวนอันดับที่ยื่นสมัคร</div>
            <div className="s-flex -wrap -gap-x color-orange">
              {phase !== 'late' && (
                <div className="s-flex gap-1">
                  <img src="/assets/img/i/i-check-orange.svg" alt="" width={16} height={16} />
                  <strong>ฟรี</strong>
                </div>
              )}
              <div className="s-flex gap-1">
                <img src="/assets/img/i/i-check-orange.svg" alt="" width={16} height={16} />
                <strong>ตัดสินใจได้ 3 ครั้ง</strong>
              </div>
              <div className="s-flex gap-1">
                <img src="/assets/img/i/i-check-orange.svg" alt="" width={16} height={16} />
                <strong>สูงสุด 7 อันดับ</strong>
              </div>
            </div>
          </li>
        </div>
      )}
      {Boolean(availables.length) && nextPriority <= 10 && (
        <>
          <br />
          <a className="btn-add cursor-pointer" onClick={mutationBusy ? undefined : onModalOpen}>
            <i className="i-add" />
            {'เลือกสาขาวิชาอันดับ'} {nextPriority}
          </a>
        </>
      )}
      <br />
      <a className={cn('btn-main', { 'cursor-pointer': !disabled, disabled })} onClick={disabled ? () => { } : _onSave}>
        {'บันทึกรายการ'}
      </a>
      {Boolean(quotaUsed) && (
        <>
          <br />
          <br />
          <a className="btn-underline cursor-pointer" onClick={mutationBusy ? undefined : onBack}>
            {'ยกเลิกและกลับไปใช้การยืนยันครั้งที่'} {quotaUsed}/3
          </a>
        </>
      )}
    </>
  )
}

export default AdmissionEdit;
